import { useState } from "react";
import { Building2, Loader2, Minus, Plus, Users } from "lucide-react";
import { useMe } from "@/lib/api";
import { useCheckout } from "@/lib/useCheckout";
import { Feature } from "@/components/PricingCards";

const SEAT = { month: 9.99, year: 99.99 };
const FIRM5 = { month: 39.99, year: 399.99 };
const fmt = (n) => `$${n.toFixed(2)}`;

const Stepper = ({ seats, setSeats }) => (
  <div className="flex items-center gap-2" data-testid="seat-stepper">
    <button data-testid="seat-minus-btn" onClick={() => setSeats(Math.max(2, seats - 1))} className="w-9 h-9 rounded-md border border-slate-300 grid place-items-center hover:bg-slate-50" aria-label="Fewer seats"><Minus size={14} /></button>
    <input
      data-testid="seat-count-input"
      type="number" min={2} max={100} value={seats}
      onChange={(e) => setSeats(Math.min(100, Math.max(2, parseInt(e.target.value, 10) || 2)))}
      className="w-16 h-9 text-center rounded-md border border-slate-300 font-semibold tabular-nums"
    />
    <button data-testid="seat-plus-btn" onClick={() => setSeats(Math.min(100, seats + 1))} className="w-9 h-9 rounded-md border border-slate-300 grid place-items-center hover:bg-slate-50" aria-label="More seats"><Plus size={14} /></button>
    <span className="text-sm text-slate-500">seats</span>
  </div>
);

export default function FirmPlans() {
  const [interval, setInterval] = useState("month");
  const [seats, setSeats] = useState(3);
  const { data: me } = useMe();
  const { checkout, busy, ready } = useCheckout();
  const isPro = me?.usage?.plan === "pro";
  const suffix = interval === "month" ? "monthly" : "yearly";
  const per = interval === "month" ? "/month" : "/year";
  const btnCls = "mt-6 h-11 w-full rounded-md bg-[#1E293B] text-white text-sm font-semibold hover:bg-blue-600 disabled:opacity-60 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2";

  return (
    <section id="firms" className="space-y-6" data-testid="firm-plans">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-amber-600 font-semibold">For law firms</p>
          <h3 className="font-heading text-2xl font-bold tracking-tight mt-2">One plan, the whole team.</h3>
          <p className="text-sm text-slate-600 mt-1">Every seat gets unlimited exports. Invite teammates by email.</p>
        </div>
        <div className="inline-flex p-1 rounded-md bg-slate-100 border border-slate-200 self-start" data-testid="firm-interval-toggle">
          {["month", "year"].map((i) => (
            <button key={i} data-testid={`firm-interval-${i}`} onClick={() => setInterval(i)} className={`h-8 px-4 rounded text-sm font-semibold transition-colors ${interval === i ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`}>
              {i === "month" ? "Monthly" : "Yearly"}
            </button>
          ))}
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-5">
        <div className="bg-white border border-slate-200 rounded-lg p-6 flex flex-col" data-testid="plan-card-team">
          <div className="flex items-center gap-2"><Users size={18} className="text-blue-600" /><h4 className="font-heading text-lg font-bold">Team - per seat</h4></div>
          <div className="mt-3 flex items-baseline gap-1">
            <span className="font-heading text-4xl font-extrabold tabular-nums" data-testid="team-total-price">{fmt(SEAT[interval] * seats)}</span>
            <span className="text-slate-500 text-sm">{per}</span>
          </div>
          <p className="text-xs text-slate-500 mt-1">{fmt(SEAT[interval])} per seat{per}. Add or remove seats anytime.</p>
          <div className="mt-5"><Stepper seats={seats} setSeats={setSeats} /></div>
          <ul className="mt-5 space-y-2.5 text-sm text-slate-700 flex-1"><Feature>Unlimited exports for every seat</Feature><Feature>Owner manages members</Feature><Feature>Change seat count anytime</Feature></ul>
          <button data-testid="plan-team-btn" disabled={!!busy || isPro || !ready} onClick={() => checkout(`team_${suffix}`, { seats })} className={btnCls}>
            {busy === `team_${suffix}` && <Loader2 size={16} className="animate-spin" />}
            {isPro ? "Already on a paid plan" : `Get ${seats} seats - ${fmt(SEAT[interval] * seats)}${per}`}
          </button>
        </div>
        <div className="bg-white border-2 border-amber-400 rounded-lg p-6 flex flex-col relative" data-testid="plan-card-firm5">
          <span className="absolute -top-3 left-6 text-[11px] font-bold uppercase tracking-wider bg-amber-400 text-[#1E293B] px-2.5 py-1 rounded-full">Save 20%</span>
          <div className="flex items-center gap-2"><Building2 size={18} className="text-amber-600" /><h4 className="font-heading text-lg font-bold">Firm - 5 seats</h4></div>
          <div className="mt-3 flex items-baseline gap-1">
            <span className="font-heading text-4xl font-extrabold tabular-nums">{fmt(FIRM5[interval])}</span>
            <span className="text-slate-500 text-sm">{per}</span>
          </div>
          <p className="text-xs text-slate-500 mt-1">Flat price for 5 seats ({fmt(FIRM5[interval] / 5)} per seat).</p>
          <ul className="mt-5 space-y-2.5 text-sm text-slate-700 flex-1"><Feature>5 seats with unlimited exports</Feature><Feature>Owner manages members</Feature><Feature>One simple invoice</Feature></ul>
          <button data-testid="plan-firm5-btn" disabled={!!busy || isPro || !ready} onClick={() => checkout(`firm5_${suffix}`)} className={btnCls}>
            {busy === `firm5_${suffix}` && <Loader2 size={16} className="animate-spin" />}
            {isPro ? "Already on a paid plan" : `Get Firm - ${fmt(FIRM5[interval])}${per}`}
          </button>
        </div>
      </div>
    </section>
  );
}
